import { useEffect, useRef } from 'react';

import { type Board } from '@air/api/types';

import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';

type UseExpandSidebarBoardOnFirstSubBoardParams = {
  board: Board;
};

/**
 * Expands a collapsed sidebar board when it gets its first child (`directDescendantBoardCount` 0 → >0).
 * Ancestor auto-expand in `WorkspaceSidebarBoardsListItem` only opens boards on the path *to* the
 * current route — it does not open the current board, or a parent that just gained a child without
 * that child becoming the current route.
 */
export const useExpandSidebarBoardOnFirstSubBoard = ({ board }: UseExpandSidebarBoardOnFirstSubBoardParams) => {
  const setTreeItem = useWorkspaceSidebarSectionStore((state) => state.setTreeItem);
  const previousDescendantBoardCountRef = useRef(board.directDescendantBoardCount ?? 0);
  const boardIdRef = useRef(board.id);

  useEffect(() => {
    const nextCount = board.directDescendantBoardCount ?? 0;

    // List rows can be reused for a different board; reset so a recycled
    // parent with children is not treated as a 0 → 1 transition.
    if (boardIdRef.current !== board.id) {
      boardIdRef.current = board.id;
      previousDescendantBoardCountRef.current = nextCount;
      return;
    }

    const previousCount = previousDescendantBoardCountRef.current;
    previousDescendantBoardCountRef.current = nextCount;

    if (previousCount === 0 && nextCount > 0) {
      // Mark touched so ancestor auto-expand does not collapse this intentional open.
      setTreeItem({ id: board.id, open: true, touched: true });
    }
  }, [board.directDescendantBoardCount, board.id, setTreeItem]);
};
